import { TERMS_DOC } from "./termsDoc";

export const LEGAL_DOCS = {
  privacy: {
    title: "Privacy Policy",
    pageTitle: "Privacy Policy | Brixigo",
    description: "How Brixigo Technologies Private Limited collects, uses, processes, stores, and protects personal data across its website and SaaS services.",
    effectiveDate: "18 August 2026",
    updated: "18 August 2026",
    intro: [
      "At Brixigo Technologies Private Limited (\"Brixigo\", \"we\", \"us\", \"our\", or \"the Company\"), we are committed to protecting the privacy and security of personal data entrusted to us. This Privacy Policy describes how Brixigo collects, uses, processes, stores, and protects personal data when you visit our website, use our SaaS products and services, communicate with us, submit an enquiry, apply for employment, or otherwise interact with us.",
      "Brixigo is a Software-as-a-Service (SaaS) provider primarily serving customers in India. Our Services may enable our customers to collect and process personal data relating to their employees, users, customers, vendors, and other individuals. Depending on the circumstances, Brixigo may process personal data on its own behalf or on behalf of its customers.",
      "This Privacy Policy applies to personal data collected by Brixigo through its website, applications, Services, business communications, recruitment activities, events, and other interactions with us.",
      "If you are an employee, user, or other individual whose personal data is processed by Brixigo on behalf of one of our customers, the relevant Brixigo customer may be the party responsible for determining the purposes and means of processing that data. In such circumstances, you may need to contact the relevant customer regarding your personal data and privacy rights.",
      "This Privacy Policy should be read together with the applicable Terms of Service, customer agreements, Data Processing Agreements, and other applicable terms governing your use of our Services.",
    ],
    sections: [
      {
        heading: "1. Information We Collect",
        paragraphs: [
          "When you interact with Brixigo, we may collect personal data and other information that is necessary for providing our Services and conducting our business.",
          "The personal data that we collect may include your name, company or organization name, job title, business email address, telephone number, physical or mailing address, username, account information, login credentials, and other information that you voluntarily provide to us.",
          "We may collect personal data when you contact us with an enquiry, request a demonstration, register for or use our Services, communicate with our support team, participate in surveys or events, provide feedback, subscribe to communications, or otherwise interact with Brixigo.",
          "Where you apply for a position with Brixigo, we may collect information contained in your resume, application form, professional profile, employment history, educational qualifications, contact details, and other information that you voluntarily provide as part of the recruitment process.",
          "You may choose not to provide certain personal data. However, where such information is necessary to provide a particular Service or respond to your request, failure to provide the information may affect our ability to provide the relevant Service.",
        ],
      },
      {
        heading: "2. Customer and Employee Data Processed Through Our SaaS Services",
        paragraphs: [
          "Brixigo provides SaaS solutions that may be used by customers to manage business and employee-related information.",
          "Depending on the Services used by a customer, the information processed through the Brixigo platform may include employee names, identification details, contact information, employment information, attendance and leave information, payroll-related information, compensation information, statutory information, and other information that the customer chooses to process through the Services.",
          "Where Brixigo processes such information on behalf of a customer, Brixigo generally acts as a processor or service provider and processes the information in accordance with the customer's instructions and applicable contractual arrangements.",
          "The customer remains responsible for determining the purposes for which such information is collected and used and for ensuring that it has an appropriate legal basis for processing the information.",
        ],
      },
      {
        heading: "3. Information We Collect Automatically",
        paragraphs: [
          "When you visit our website or use our Services, certain information may be collected automatically through your browser, device, application, or our servers.",
          "This information may include your Internet Protocol (IP) address, browser type, browser language, operating system, device information, date and time of access, pages viewed, navigation information, application logs, technical information, usage information, and information relating to your interaction with our website or Services.",
          "We use this information to operate and maintain our website and Services, monitor performance, identify technical issues, maintain security, prevent misuse, understand usage patterns, and improve our products and user experience.",
        ],
      },
      {
        heading: "4. Cookies and Similar Technologies",
        paragraphs: [
          "Brixigo uses cookies and similar technologies on its website and, where applicable, its Services.",
          "Cookies are small files placed on your browser or device that allow a website to recognize your browser and remember certain information. We may use cookies for purposes such as maintaining website functionality, authentication and session management, security, remembering preferences, understanding website usage, measuring performance, and improving the user experience.",
          "Brixigo also uses Google Analytics to understand how visitors interact with our website. Google Analytics may collect information such as pages visited, approximate geographic information, browser and device information, referring websites, time spent on pages, traffic sources, and other technical and usage information.",
          "Where required under applicable law, Brixigo will obtain appropriate consent before using non-essential cookies or analytics technologies.",
          "You may control or disable cookies through your browser settings. Disabling certain cookies may, however, affect the availability or functionality of certain portions of our website or Services.",
        ],
      },
      {
        heading: "5. Log Information",
        paragraphs: [
          "When you access or use our Services, our servers may automatically record certain information for operational, security, and technical purposes.",
          "Such information may include IP addresses, browser and device information, access dates and times, authentication events, requests made to our servers, application events, error information, security events, and other technical information.",
          "We use this information to maintain the security and reliability of our Services, troubleshoot technical problems, investigate security incidents, monitor system performance, and improve our Services.",
        ],
      },
      {
        heading: "6. Information from Third Parties",
        paragraphs: [
          "Brixigo may receive personal information about you from third parties, including customers, business partners, resellers, event organizers, recruitment agencies, referral partners, and other parties with whom we have a legitimate business relationship.",
          "Such information may include your name, company name, designation, business email address, telephone number, address, professional information, or other information relevant to the particular relationship.",
          "Where applicable, Brixigo expects third parties providing personal information to have obtained such information lawfully and to have an appropriate basis or authorization for sharing the information with us.",
        ],
      },
      {
        heading: "7. How We Use Your Information",
        paragraphs: [
          "Brixigo uses personal data for legitimate business and operational purposes and in accordance with applicable law.",
          "We may use personal data to provide, operate, maintain, and support our Services; create and administer user accounts; authenticate users; respond to enquiries and support requests; process transactions; communicate with customers and users; troubleshoot technical problems; maintain security; prevent fraud and misuse; improve our products and Services; develop new features; and understand how our Services are used.",
          "We may also use personal data to communicate with you about administrative matters, service updates, security matters, changes to our Services, and other information necessary for the operation of your account or our relationship with you.",
          "Where permitted by applicable law, we may use your contact information to send information about our products, services, events, or other business offerings. Every promotional communication will provide an appropriate means to opt out of future marketing communications.",
          "We may also process personal data where necessary to comply with applicable law, respond to lawful requests, enforce our agreements, protect our legal rights, investigate fraud or security incidents, or protect the rights, property, or safety of Brixigo, our customers, users, or others.",
        ],
      },
      {
        heading: "8. Legal Basis for Processing",
        paragraphs: [
          "Depending on the nature of the processing and applicable law, Brixigo may process personal data on the basis of consent, performance of a contract, compliance with a legal obligation, legitimate interests where permitted by law, or another lawful basis available under applicable legislation.",
          "Where processing is based on consent, you may withdraw your consent subject to applicable law and any consequences that may arise from such withdrawal.",
          "Brixigo will process personal data in accordance with applicable Indian data protection and privacy laws, including the Digital Personal Data Protection Act, 2023 and applicable rules and regulations thereunder, to the extent applicable to the relevant processing activity. The DPDP Act establishes the framework governing processing of digital personal data in India and provides rights and obligations for Data Principals and Data Fiduciaries.",
        ],
      },
      {
        heading: "9. Data Security",
        paragraphs: [
          "Brixigo takes reasonable and appropriate technical and organizational measures to protect personal data against unauthorized access, alteration, disclosure, misuse, loss, or destruction.",
          "Our security measures may include encryption of data in transit, encryption of data at rest where appropriate, authentication controls, role-based access controls, restricted administrative access, network security, logging and monitoring, backup and recovery procedures, and other security measures appropriate to the nature of the information and the Services.",
          "Brixigo hosts its primary SaaS infrastructure using DigitalOcean, with the primary infrastructure located in the Bangalore (BLR1), India region.",
          "Access to personal data within Brixigo is restricted to authorized personnel and systems where such access is reasonably necessary for providing, maintaining, securing, or supporting our Services or for complying with applicable legal obligations.",
          "Although we take reasonable measures to protect personal data, no method of transmission over the Internet or method of electronic storage can be guaranteed to be completely secure.",
        ],
      },
      {
        heading: "10. Information Sharing",
        paragraphs: [
          "Brixigo does not sell personal data for monetary consideration.",
          "We may share personal data with trusted third-party service providers where such sharing is necessary to operate our business or provide our Services. Such providers may assist with cloud infrastructure, hosting, storage, backup, security, monitoring, analytics, communications, customer support, maintenance, payment processing, and other business or technical functions.",
          "Brixigo may also share personal data with its affiliates or business partners where reasonably necessary for legitimate business, operational, support, or compliance purposes.",
          "Where Brixigo engages a third party to process personal data on its behalf, we seek to ensure that the third party processes the information only for the relevant purpose and maintains appropriate confidentiality and security measures.",
          "Brixigo may disclose personal data where we have a good-faith belief that such disclosure is reasonably necessary to comply with applicable law, regulation, legal process, or governmental request; enforce our Terms of Service or other agreements; detect, prevent, or address fraud, security, or technical issues; or protect the rights, property, or safety of Brixigo, our customers, users, or the public.",
          "If Brixigo is involved in a merger, acquisition, restructuring, financing, sale of assets, or other corporate transaction, personal data may form part of the assets transferred in connection with that transaction, subject to applicable law.",
        ],
      },
      {
        heading: "11. Third-Party Services and Websites",
        paragraphs: [
          "Our website and Services may contain links to websites, applications, or services operated by third parties.",
          "Brixigo may also use third-party services, including Google Analytics and DigitalOcean, in connection with the operation of its website and SaaS Services.",
          "Third-party services operate under their own terms and privacy policies. Brixigo does not control the privacy practices of independent third-party websites or services and is not responsible for their privacy practices.",
          "We encourage you to review the privacy policies applicable to third-party services before providing personal information to them.",
        ],
      },
      {
        heading: "12. Data Retention",
        paragraphs: [
          "Brixigo retains personal data only for as long as reasonably necessary for the purposes for which it was collected or processed, or as required for legal, regulatory, contractual, security, accounting, dispute-resolution, or legitimate business purposes.",
          "Customer data processed through our SaaS Services will generally be retained in accordance with the applicable customer agreement. Following termination of the relevant Services, customer data may generally be deleted within 30 to 90 days, subject to contractual requirements, legal obligations, and applicable backup cycles.",
          "System and customer backups are generally retained for approximately 30 to 90 days, depending on the applicable backup and recovery configuration.",
          "Information relating to customer accounts, support communications, recruitment, marketing, financial records, security logs, and other business records may be retained for different periods depending on the purpose and applicable legal or business requirements.",
          "Where personal data is no longer required, Brixigo will take reasonable steps to delete, anonymize, or securely dispose of such information.",
        ],
      },
      {
        heading: "13. Data Processing and Storage in India",
        paragraphs: [
          "Brixigo primarily provides its Services to customers in India. Our Services may be hosted and operated using cloud infrastructure and third-party technology services. Certain third-party services used by Brixigo, such as analytics, communications, security, monitoring, or other technology services, may process limited information in locations outside India depending on the infrastructure and services provided by those third parties.",
          "Where personal data is transferred or processed outside India, Brixigo will take appropriate measures as required by applicable law. Customers with specific contractual data residency or processing requirements should refer to their applicable agreement or Data Processing Agreement with Brixigo.",
        ],
      },
      {
        heading: "14. Your Privacy Rights",
        paragraphs: [
          "Subject to applicable law, individuals may have rights in relation to their personal data, including the right to obtain information about processing, access personal data, request correction of inaccurate information, request erasure where legally available, withdraw consent where processing is based on consent, and exercise other rights available under applicable data protection laws.",
          "The scope and availability of these rights may depend on the applicable law and the circumstances in which Brixigo processes the personal data.",
          "Where Brixigo processes personal data solely on behalf of one of its customers, requests relating to such data may need to be directed to the relevant customer.",
          "The applicable Indian data protection framework provides rights to Data Principals and establishes mechanisms through which such rights may be exercised.",
        ],
      },
      {
        heading: "15. Access, Correction, Deletion and Privacy Requests",
        paragraphs: [
          "If you wish to access, correct, update, modify, delete, or otherwise exercise a privacy right in relation to personal data processed by Brixigo, or if you have a privacy-related complaint or concern, you may contact us at:",
        ],
        contact: {
          name: "Brixigo Technologies Private Limited",
          email: "hr@brixigo.com",
        },
        afterParagraphs: [
          "Brixigo currently does not have a separately appointed Data Protection Officer. hr@brixigo.com is the designated contact point for privacy-related requests, questions, and grievances.",
          "We may request reasonable information to verify your identity before processing certain requests. We will respond to valid requests in accordance with applicable law.",
        ],
      },
      {
        heading: "16. Marketing Communications",
        paragraphs: [
          "Brixigo may from time to time send communications regarding its products, Services, events, updates, or other offerings where permitted by applicable law.",
          "You may opt out of receiving promotional communications at any time by using the unsubscribe mechanism provided in the relevant communication or by contacting us at hr@brixigo.com.",
          "Please note that even if you opt out of promotional communications, we may continue to send essential administrative, transactional, security, account-related, or service communications where necessary.",
        ],
      },
      {
        heading: "17. Children's Privacy",
        paragraphs: [
          "Brixigo's website and SaaS Services are intended primarily for business and professional use and are not directed toward children.",
          "Brixigo does not knowingly collect personal data from children in violation of applicable law. If you believe that a child has provided personal data to Brixigo without appropriate authorization, please contact us at hr@brixigo.com.",
          "Upon receiving such a request, Brixigo will take reasonable steps to investigate and, where appropriate and legally required, delete the relevant information.",
        ],
      },
      {
        heading: "18. Recruitment and Employment Information",
        paragraphs: [
          "Where you apply for employment with Brixigo, we may use the personal information provided by you to evaluate your application, communicate with you regarding recruitment, conduct interviews and assessments, consider you for current or future employment opportunities, and maintain appropriate recruitment records.",
          "Unless a longer period is required or permitted by applicable law, Brixigo may retain recruitment information for approximately two years following the relevant recruitment process.",
        ],
      },
      {
        heading: "19. Changes to this Privacy Policy",
        paragraphs: [
          "Brixigo may modify or update this Privacy Policy from time to time to reflect changes in our Services, business practices, technology, legal requirements, or privacy and security practices.",
          "Any changes will be posted on this page along with the revised Effective Date or Last Updated date. Where required by applicable law, Brixigo may provide additional notice regarding material changes.",
          "Your continued use of our website or Services following the publication of an updated Privacy Policy will constitute acknowledgment of the updated policy to the extent permitted by applicable law.",
        ],
      },
      {
        heading: "20. Grievance Redressal and Contact Us",
        paragraphs: [
          "If you have any questions, complaints, concerns, or requests regarding this Privacy Policy or the manner in which Brixigo collects, uses, or processes personal data, please contact us at:",
        ],
        contact: {
          name: "Brixigo Technologies Private Limited",
          email: "hr@brixigo.com",
          address: "H. No. 13-4-38, Babu Camp, Bellampalli, Telangana – 504251, India",
          emailLabel: "Privacy / Grievance Contact",
        },
        afterParagraphs: [
          "Brixigo will review and address privacy-related requests and grievances in accordance with applicable law and its internal procedures.",
        ],
      },
      {
        heading: "21. Effective Date",
        paragraphs: [
          "This Privacy Policy is effective from 18 August 2026.",
          "Last Updated: 18 August 2026",
        ],
        contact: {
          name: "Brixigo Technologies Private Limited",
          address: "H. No. 13-4-38, Babu Camp, Bellampalli, Telangana – 504251, India",
        },
      },
    ],
  },
  terms: TERMS_DOC,
  refund: {
    title: "Cancellation & Refund Policy",
    pageTitle: "Cancellation & Refund Policy | Brixigo",
    description: "Cancellation and refund terms for Brixigo HRMS, payroll, and related subscription services.",
    effectiveDate: "20 August 2026",
    updated: "20 August 2026",
    intro: [
      "This Cancellation & Refund Policy (\"Policy\") applies to the products and services provided by Brixigo Technologies Private Limited (\"Brixigo\", \"Company\", \"we\", \"us\", or \"our\"), including our HRMS, payroll, employee management, attendance, leave management, reporting, and related software services (collectively, the \"Services\").",
      "By purchasing, subscribing to, or using our Services, you acknowledge that you have read and agree to this Policy.",
    ],
    sections: [
      {
        heading: "1. Subscription Cancellation",
        paragraphs: [
          "Customers may cancel their subscription at any time by contacting our support team or, where available, using the cancellation option provided within the Brixigo platform.",
          "Unless otherwise agreed in writing:",
        ],
        list: [
          "Cancellation will take effect at the end of the customer's current billing period.",
          "Customers may continue using the subscribed Services until the end of the paid billing period, subject to our Terms of Use.",
          "Cancellation will prevent the subscription from renewing for the next billing period.",
          "Cancelling a subscription does not automatically entitle the customer to a refund for the unused portion of the current billing period.",
          "Any outstanding amounts due to Brixigo remain payable after cancellation.",
        ],
      },
      {
        heading: "2. Monthly Subscriptions",
        paragraphs: [
          "For monthly subscriptions, customers may cancel at any time.",
          "The subscription will generally remain active until the end of the current monthly billing period. Amounts already paid for the current billing period are generally non-refundable solely because the customer has stopped using the Services.",
        ],
      },
      {
        heading: "3. Annual Subscriptions",
        paragraphs: [
          "Customers may cancel an annual subscription at any time; however, cancellation does not automatically result in a refund for the unused portion of the annual subscription.",
          "Any refund request for an annual subscription will be reviewed according to this Policy and the circumstances of the request.",
        ],
      },
      {
        heading: "4. Refund Eligibility",
        paragraphs: [
          "A refund may be considered in the following circumstances:",
        ],
        subsections: [
          {
            heading: "4.1 Duplicate Payment",
            paragraphs: [
              "If the same invoice or subscription has been paid more than once due to an accidental or technical duplicate transaction, the duplicate payment may be refunded after verification.",
            ],
          },
          {
            heading: "4.2 Successful Payment but Service Not Activated",
            paragraphs: [
              "If payment has been successfully completed but the purchased subscription cannot be activated because of a technical issue attributable to Brixigo, we will make reasonable efforts to resolve the issue.",
              "If we are unable to provide the purchased Service within a reasonable period, the affected payment may be eligible for a refund.",
            ],
          },
          {
            heading: "4.3 Significant Service Unavailability",
            paragraphs: [
              "If a significant technical problem attributable to Brixigo prevents customers from reasonably accessing the paid Service for a prolonged period, Brixigo may, at its discretion, provide a service extension, service credit, or refund for the affected period.",
              "Routine maintenance, scheduled maintenance, internet connectivity problems, third-party outages, or circumstances beyond Brixigo's reasonable control generally do not qualify for a refund.",
            ],
          },
          {
            heading: "4.4 Unauthorized Transactions",
            paragraphs: [
              "If you believe an unauthorized transaction has been made using your payment method, please contact us immediately with the relevant transaction details.",
              "We may investigate the transaction and take appropriate action after verification.",
            ],
          },
          {
            heading: "4.5 Other Exceptional Circumstances",
            paragraphs: [
              "Brixigo may consider refund requests involving exceptional circumstances on a case-by-case basis.",
              "Approval of such requests is subject to the circumstances of the case and Brixigo's applicable policies.",
            ],
          },
        ],
      },
      {
        heading: "5. Non-Refundable Situations",
        paragraphs: [
          "Unless otherwise required by applicable law, refunds will generally not be provided in the following circumstances:",
        ],
        list: [
          "The customer changes their mind after purchasing a subscription.",
          "The customer does not use the Services after purchase.",
          "The customer cancels after the applicable billing period has started.",
          "The customer fails to use the available features during the subscription period.",
          "The customer purchases a plan that does not meet their business requirements.",
          "The customer fails to configure or use the Services despite having an active subscription.",
          "The customer fails to provide information required to configure or operate the Services.",
          "The customer requests a refund solely because they have decided to use another HRMS or payroll provider.",
          "The account has been suspended or terminated because of a violation of our Terms of Use or applicable policies.",
          "Problems are caused by the customer's device, internet connection, browser, third-party software, infrastructure, or other systems outside Brixigo's reasonable control.",
          "The customer has already used or consumed a service for which the applicable subscription terms specify that the payment is non-refundable.",
        ],
      },
      {
        heading: "6. Payroll and HRMS Services",
        paragraphs: [
          "Brixigo provides software that may assist organizations with payroll, employee records, attendance, leave, salary calculations, reports, and other HR-related processes.",
          "Customers are responsible for reviewing and verifying employee information, salary information, attendance data, leave data, statutory information, configurations, and other information entered into or generated through the Platform before finalizing payroll or taking action based on such information.",
          "If incorrect results arise because of inaccurate, incomplete, or incorrectly configured information supplied or approved by the customer, the customer will generally not be entitled to a refund on that basis.",
          "If an error is caused directly by a technical defect in the Brixigo Platform, customers should contact our support team as soon as possible. We will investigate the issue and, where appropriate, assist with correction or resolution.",
        ],
      },
      {
        heading: "7. Plan Upgrades",
        paragraphs: [
          "Where plan upgrades are available, customers may upgrade their subscription according to the options provided by Brixigo.",
          "An upgrade may take effect immediately or from the next billing cycle depending on the applicable billing arrangement.",
          "Any additional charges resulting from an upgrade will be calculated according to the applicable pricing and billing terms.",
        ],
      },
      {
        heading: "8. Plan Downgrades",
        paragraphs: [
          "Where plan downgrades are available, a downgrade may take effect from the beginning of the next billing cycle unless otherwise specified.",
          "Downgrading a plan does not normally result in a refund for the unused portion of the current billing period.",
          "Customers should ensure that the selected plan supports their required employee count, features, payroll requirements, integrations, and other business needs before downgrading.",
        ],
      },
      {
        heading: "9. Automatic Renewal",
        paragraphs: [
          "Where recurring billing is enabled, subscriptions may automatically renew at the end of the applicable billing period.",
          "Customers are responsible for cancelling their subscription before the next billing date if they do not wish to renew.",
          "If a subscription has already renewed, the customer may contact Brixigo and request cancellation and, where applicable, a refund.",
          "Any such refund request will be reviewed based on the circumstances, including the time elapsed since renewal and whether the renewed Services have been used.",
        ],
      },
      {
        heading: "10. Failed or Pending Payments",
        paragraphs: [
          "If a payment fails, is declined, or remains pending, the subscription may not be activated or renewed until successful payment confirmation is received.",
          "If money has been debited from the customer's bank account but the payment has not been successfully reflected in the Brixigo account, the customer should contact us with the transaction details.",
          "In some cases, the payment gateway or bank may automatically reverse the transaction. The time required for such reversal depends on the applicable bank or payment service provider.",
        ],
      },
      {
        heading: "11. Payment Successful but Subscription Not Created",
        paragraphs: [
          "If the customer's account has been debited but the corresponding subscription or payment status is not reflected on the Brixigo Platform, the customer should contact our support team.",
          "We will verify the transaction using the available payment and transaction information.",
          "If the payment is confirmed but the purchased Service cannot be provided, Brixigo may either activate the applicable Service or process an eligible refund.",
        ],
      },
      {
        heading: "12. Incorrect or Excess Payments",
        paragraphs: [
          "If a customer accidentally pays more than the amount due or makes an additional payment for the same invoice, the customer should notify us as soon as possible.",
          "After verification, the eligible excess amount may be refunded through the original payment method.",
        ],
      },
      {
        heading: "13. Refund Amount",
        paragraphs: [
          "If a refund is approved, the amount will depend on the reason for the refund and the circumstances of the transaction.",
          "Examples include:",
        ],
        list: [
          "A verified duplicate payment may generally be refunded in full.",
          "A payment for a Service that could not be activated due to a Brixigo technical issue may be eligible for a full or appropriate refund.",
          "A qualifying prolonged service interruption may result in a service credit, extension, partial refund, or other appropriate remedy at Brixigo's discretion.",
          "Other approved refund requests will be handled according to the specific circumstances.",
        ],
        afterParagraphs: [
          "Unused subscription time does not automatically create a right to a pro-rata refund.",
        ],
      },
      {
        heading: "14. Refund Processing Time",
        paragraphs: [
          "Once a refund is approved, Brixigo will initiate the refund within a reasonable period.",
          "The time required for the refunded amount to appear in the customer's bank account, card account, UPI account, or other payment method may vary depending on the applicable bank, payment gateway, card issuer, or financial institution.",
          "Brixigo is not responsible for delays caused by banks, payment processors, card networks, or other third-party financial institutions after the refund has been initiated.",
        ],
      },
      {
        heading: "15. Refund Method",
        paragraphs: [
          "Approved refunds will generally be processed to the original payment method used for the transaction.",
          "Where required for verification or processing, Brixigo may request additional information from the customer.",
          "We may decline to process a refund through an alternative payment method where doing so could create security, fraud, accounting, or compliance concerns.",
        ],
      },
      {
        heading: "16. Third-Party Services",
        paragraphs: [
          "Certain Brixigo features may depend on third-party services, payment gateways, banking systems, communication services, integrations, or other external providers.",
          "Brixigo will make reasonable efforts to maintain such integrations, but outages or failures caused by third parties may not qualify for a refund.",
          "Where a third-party service interruption materially affects the Brixigo Services, we will make reasonable efforts to restore or provide an appropriate alternative where practicable.",
        ],
      },
      {
        heading: "17. Customer Data After Cancellation",
        paragraphs: [
          "Cancellation, expiry, suspension, or termination of a subscription may affect the customer's ability to access employee records, payroll information, reports, documents, and other data stored on the Brixigo Platform.",
          "Customers are responsible for exporting or downloading Customer Data and other information they are entitled to retain before their subscription ends, where such functionality is available.",
          "Following cancellation, expiry, suspension, or termination, the handling, retention, export, and deletion of Customer Data will be governed by the Brixigo Terms of Use, Privacy Policy, applicable customer agreements, applicable Data Processing Agreements, and applicable law.",
          "Brixigo may retain Customer Data for the periods and purposes permitted or required under those applicable terms, including where retention is necessary for legal, regulatory, contractual, security, accounting, dispute-resolution, backup, or other legitimate business purposes.",
          "For further information regarding the retention and deletion of personal data and Customer Data, customers should refer to the Brixigo Privacy Policy and applicable Terms of Use.",
        ],
      },
      {
        heading: "18. Account Suspension or Termination",
        paragraphs: [
          "If an account is suspended or terminated because of fraud, misuse of the Platform, violation of our Terms of Use, non-payment, or other prohibited activity, refunds will be handled in accordance with this Policy, applicable agreements, and applicable law.",
          "Termination or suspension does not remove any outstanding payment obligations incurred before the effective date of termination.",
        ],
      },
      {
        heading: "19. Refund Request Procedure",
        paragraphs: [
          "To request a cancellation or refund, contact our support team using the details below.",
          "The request should include, where applicable:",
        ],
        list: [
          "Company or customer name",
          "Registered email address",
          "Brixigo account details",
          "Invoice number",
          "Payment or transaction ID",
          "Date of payment",
          "Amount paid",
          "Reason for requesting cancellation or refund",
          "Relevant supporting information",
        ],
        afterParagraphs: [
          "We may request additional information to verify the transaction and eligibility.",
        ],
      },
      {
        heading: "20. Refund Review",
        paragraphs: [
          "All refund requests are subject to verification.",
          "Brixigo may review relevant account information, subscription status, invoices, payment records, usage information, transaction information, technical records, and other relevant information when assessing a refund request.",
          "Submitting a refund request does not guarantee approval of a refund.",
        ],
      },
      {
        heading: "21. Chargebacks and Payment Disputes",
        paragraphs: [
          "Customers are encouraged to contact Brixigo before initiating a chargeback or payment dispute so that we can investigate and attempt to resolve the issue.",
          "Where a chargeback is initiated, Brixigo may provide relevant transaction and account information to the applicable payment provider, bank, or payment network in accordance with applicable rules and law.",
          "Fraudulent or abusive chargebacks may be investigated and handled appropriately.",
        ],
      },
      {
        heading: "22. Changes to Pricing or Services",
        paragraphs: [
          "Brixigo may modify its subscription plans, pricing, features, or billing arrangements from time to time.",
          "Where required by applicable law or contractual terms, customers will be notified of material changes.",
          "Changes to pricing, features, or plans do not automatically create a right to a refund for amounts already paid.",
        ],
      },
      {
        heading: "23. Applicable Legal Rights",
        paragraphs: [
          "Nothing in this Policy is intended to limit any rights or remedies that cannot legally be excluded under applicable law.",
          "Where applicable law provides a customer with a mandatory right to a cancellation, refund, or other remedy, that right will continue to apply.",
        ],
      },
      {
        heading: "24. Changes to This Policy",
        paragraphs: [
          "Brixigo may update this Cancellation & Refund Policy from time to time.",
          "Any updated version will be published on our website with a revised \"Last Updated\" date.",
          "Customers are encouraged to review this Policy periodically.",
        ],
      },
      {
        heading: "25. Contact Us",
        paragraphs: [
          "For questions, cancellation requests, billing issues, or refund requests, please contact:",
        ],
        contact: {
          name: "Brixigo Technologies Private Limited",
          email: "support@brixigo.com",
          phone: "+91 83091 96410",
          phoneHref: "tel:+918309196410",
          address: "H. No. 13-4-38, Babu Camp, Bellampalli, Telangana – 504251, India",
        },
        afterParagraphs: [
          "We will make reasonable efforts to review and respond to cancellation and refund requests promptly.",
        ],
      },
    ],
  },
  delivery: {
    title: "Service Delivery Policy",
    pageTitle: "Service Delivery Policy | Brixigo",
    description: "How Brixigo Technologies Private Limited delivers cloud-based HRMS and payroll software on a SaaS basis.",
    effectiveDate: "20 August 2026",
    updated: "20 August 2026",
    intro: [
      "Brixigo Technologies Private Limited (\"Brixigo\", \"we\", \"us\", or \"our\") provides cloud-based Human Resource Management System (HRMS) and payroll software on a Software-as-a-Service (SaaS) basis.",
      "As our Services are delivered digitally, we do not ship or deliver physical products.",
    ],
    note: "Brixigo provides digital SaaS services and does not ship physical products. Accordingly, no physical shipping charges or delivery arrangements apply to our Services.",
    sections: [
      {
        heading: "1. Digital Service Delivery",
        paragraphs: [
          "After successful subscription and payment, access to the applicable Brixigo HRMS and payroll services is provided electronically through the Brixigo Platform.",
          "Depending on the applicable service or subscription, customers may receive:",
        ],
        list: [
          "Access to the Brixigo HRMS and payroll platform",
          "Account activation or login credentials",
          "Subscription confirmation",
          "Onboarding or setup instructions",
          "Access to subscribed features and services",
        ],
        afterParagraphs: [
          "No physical products are shipped to customers.",
        ],
      },
      {
        heading: "2. Service Activation",
        paragraphs: [
          "For subscriptions that support immediate activation, access may be provided after successful payment confirmation.",
          "For subscriptions requiring account verification, configuration, onboarding, or setup, Brixigo may contact the customer to complete the required process before the Services are activated.",
          "The time required for setup may vary depending on the customer's requirements and the information required to configure the Services.",
        ],
      },
      {
        heading: "3. Payment Successful but Access Not Received",
        paragraphs: [
          "If payment has been successfully completed but the customer has not received access to the subscribed Services, the customer should contact us at:",
        ],
        contact: {
          email: "support@brixigo.com",
        },
        afterParagraphs: [
          "Please provide the registered email address and payment or transaction ID so that we can verify the transaction and assist with activation.",
        ],
      },
      {
        heading: "4. Service Availability",
        paragraphs: [
          "Brixigo provides its Services through the internet. Customers are responsible for maintaining a suitable internet connection, compatible device, browser, and other necessary infrastructure to access the Platform.",
          "Temporary unavailability may occur due to scheduled maintenance, emergency maintenance, technical issues, or circumstances outside Brixigo's reasonable control.",
        ],
      },
      {
        heading: "5. Subscription Period",
        paragraphs: [
          "Access to the Services is provided for the subscription period selected and paid for by the customer.",
          "Access may be restricted or discontinued when the subscription expires, is cancelled, or is terminated in accordance with the applicable Terms of Use.",
        ],
      },
      {
        heading: "6. Customer Information and Setup",
        paragraphs: [
          "Customers are responsible for providing accurate and complete information required for account creation, configuration, HRMS setup, and payroll processing.",
          "Delays caused by incomplete, inaccurate, or late information provided by the customer may affect the activation or configuration of the Services.",
        ],
      },
      {
        heading: "7. Support",
        paragraphs: [
          "If you experience any issue accessing or using the Services, please contact our support team:",
        ],
        contact: {
          email: "support@brixigo.com",
          phone: "+91 83091 96410",
          phoneHref: "tel:+918309196410",
        },
        afterParagraphs: [
          "We will make reasonable efforts to assist customers with service activation and access-related issues.",
        ],
      },
      {
        heading: "8. Contact Us",
        contact: {
          name: "Brixigo Technologies Private Limited",
          email: "support@brixigo.com",
          phone: "+91 83091 96410",
          phoneHref: "tel:+918309196410",
          addressLabel: "Registered Office",
          address: "H. No. 13-4-38, Babu Camp, Bellampalli, Telangana – 504251, India",
        },
      },
    ],
  },
};
